<template>
  <v-card>
    <!-- Compact Header with Inline Filters -->
    <v-card-title class="pb-3">
      <div class="d-flex align-center w-100">
        <!-- Title Section -->
        <div class="d-flex align-center mr-6">
          <v-icon left color="primary" size="24"
            >mdi-map-marker-multiple</v-icon
          >
          <span class="text-h6 font-weight-medium">Provider Referrals Map</span>
        </div>

        <!-- Filters Section -->
        <div class="d-flex align-center flex-wrap">
          <!-- Location -->
          <div class="mr-5">
            <v-select
              :items="locationOptions"
              :value="location"
              @change="changeLocation"
              dense
              hide-details
              label="Location"
              style="min-width: 120px"
            ></v-select>
          </div>

          <!-- Search -->
          <div class="mr-5">
            <v-form @submit.prevent="searchAddress">
              <v-text-field
                v-model="search"
                placeholder="Search address..."
                prepend-inner-icon="mdi-magnify"
                dense
                hide-details
                clearable
                style="min-width: 220px"
                @click:clear="
                  search = '';
                  searchMarker = false;
                "
              ></v-text-field>
            </v-form>
          </div>

          <!-- Priority Filter -->
          <div class="mr-5">
            <v-select
              v-model="selectedPriorityLevels"
              :items="priorityLevelOptions"
              label="Priority"
              multiple
              chips
              dense
              hide-details
              clearable
              small-chips
              style="min-width: 180px"
            >
              <template v-slot:selection="{ item, index }">
                <v-chip
                  v-if="index < 2"
                  x-small
                  :color="getPriorityChipColor(item.value)"
                  dark
                >
                  {{ item.value }}
                </v-chip>
                <span v-if="index === 2" class="text-caption grey--text"
                  >+{{ selectedPriorityLevels.length - 2 }}</span
                >
              </template>
            </v-select>
          </div>

          <!-- Service Filters -->
          <div class="d-flex align-center mr-5">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-checkbox
                  v-model="filterAcceptsLien"
                  dense
                  hide-details
                  class="ma-0 pa-0 mr-2"
                  v-bind="attrs"
                  v-on="on"
                ></v-checkbox>
              </template>
              <span>Accepts Lien</span>
            </v-tooltip>
            <span class="text-caption">Lien</span>
          </div>

          <div class="d-flex align-center mr-5">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-checkbox
                  v-model="filterAcceptsHealthInsurance"
                  dense
                  hide-details
                  class="ma-0 pa-0 mr-2"
                  v-bind="attrs"
                  v-on="on"
                ></v-checkbox>
              </template>
              <span>Accepts Health Insurance</span>
            </v-tooltip>
            <span class="text-caption">Insurance</span>
          </div>

          <!-- Clear & Counter -->
          <div class="d-flex align-center">
            <v-btn
              v-if="hasActiveFilters"
              @click="clearAllFilters"
              x-small
              text
              color="grey"
              class="mr-3"
            >
              <v-icon small>mdi-close</v-icon>
              Clear
            </v-btn>

            <v-chip
              v-if="hasActiveFilters"
              x-small
              color="primary"
              text-color="white"
            >
              {{ activeFilterCount }} active
            </v-chip>
          </div>
        </div>
      </div>
    </v-card-title>

    <!-- Priority Legend -->
    <v-divider class="mx-4"></v-divider>
    <v-card-subtitle class="py-2">
      <div class="d-flex align-center">
        <span class="text-caption grey--text font-weight-medium mr-4">
          <v-icon small class="mr-1">mdi-palette</v-icon>
          Priority Legend:
        </span>
        <div class="d-flex align-center mr-4">
          <div class="mini-legend-dot green mr-1"></div>
          <span class="text-caption">1-Highest</span>
        </div>
        <div class="d-flex align-center mr-4">
          <div class="mini-legend-dot blue mr-1"></div>
          <span class="text-caption">2-High</span>
        </div>
        <div class="d-flex align-center mr-4">
          <div class="mini-legend-dot yellow mr-1"></div>
          <span class="text-caption">3-Medium</span>
        </div>
        <div class="d-flex align-center mr-4">
          <div class="mini-legend-dot red mr-1"></div>
          <span class="text-caption">4-Low</span>
        </div>
        <div class="d-flex align-center mr-4">
          <div class="mini-legend-dot black mr-1"></div>
          <span class="text-caption">5-Do Not Use</span>
        </div>
        <div class="d-flex align-center">
          <div class="mini-legend-dot purple mr-1"></div>
          <span class="text-caption">None</span>
        </div>
      </div>
    </v-card-subtitle>

    <!-- Map Section -->
    <v-card-text class="pa-0">
      <div>
        <div v-if="!contacts" class="text-center py-8">
          <v-progress-circular
            indeterminate
            color="primary"
          ></v-progress-circular>
          <div class="mt-2">Loading map data...</div>
        </div>
        <div v-else-if="contacts.length === 0" class="text-center py-8">
          <v-icon large color="grey">mdi-map-marker-off</v-icon>
          <div class="mt-2">No contacts found</div>
        </div>
        <GmapMap
          v-else
          :center="mapCenter"
          :zoom="11"
          map-type-id="roadmap"
          style="width: 100%; height: 500px"
          :options="mapOptions"
        >
          <GmapMarker
            :key="index"
            v-for="(m, index) in markers"
            :position="m.position"
            :clickable="true"
            :draggable="false"
            :title="m.title"
            :icon="m.icon"
            @click="showInfo(m)"
          />
          <GmapInfoWindow
            v-if="showInfoWindow"
            :position="infoWindowPosition"
            @closeclick="closeInfo"
          >
            <div style="min-width: 250px;">
              <div class="mb-2">
                <b>{{ infoWindowContact.name }}</b>
                <v-chip
                  v-if="infoWindowContact.priority_level"
                  :color="getPriorityColor(infoWindowContact.priority_level)"
                  small
                  class="ml-2"
                  dark
                >
                  Level {{ infoWindowContact.priority_level }}
                </v-chip>
              </div>
              <div class="mt-2">
                <strong>Address:</strong> {{ infoWindowContact.address }},
                {{ infoWindowContact.city }}, {{ infoWindowContact.state }}
                {{ infoWindowContact.zip }}
              </div>
              <div><strong>Phone:</strong> {{ infoWindowContact.phone }}</div>
              <div><strong>Email:</strong> {{ infoWindowContact.email }}</div>
              <div class="mt-2">
                <div class="d-flex align-center">
                  <v-icon
                    :color="
                      infoWindowContact.accepts_lien ? 'success' : 'error'
                    "
                    small
                    class="mr-1"
                  >
                    {{
                      infoWindowContact.accepts_lien ? "mdi-check" : "mdi-close"
                    }}
                  </v-icon>
                  <span>Accepts Lien</span>
                </div>
                <div class="d-flex align-center">
                  <v-icon
                    :color="
                      infoWindowContact.accepts_health_insurance
                        ? 'success'
                        : 'error'
                    "
                    small
                    class="mr-1"
                  >
                    {{
                      infoWindowContact.accepts_health_insurance
                        ? "mdi-check"
                        : "mdi-close"
                    }}
                  </v-icon>
                  <span>Accepts Health Insurance</span>
                </div>
              </div>
              <div class="mt-2">
                <div>
                  <strong>To:</strong>
                  {{ infoWindowContact.outboundReferrals }}
                </div>
                <div>
                  <strong>From:</strong>
                  {{ infoWindowContact.inboundReferrals }}
                </div>
              </div>
              <div class="mt-3">
                <strong>Referral Notes:</strong>
                <v-textarea
                  v-model="editableReferralNotes"
                  rows="3"
                  dense
                  outlined
                  hide-details
                  :readonly="notesReadonly"
                  placeholder="Click to add referral instructions, email addresses, etc."
                  class="mt-1"
                  :class="notesSaveStatus"
                  style="font-size: 13px;"
                  @click="enableNotesEditing"
                ></v-textarea>
                <div class="d-flex align-center mt-1">
                  <v-btn
                    x-small
                    color="primary"
                    :loading="savingNotes"
                    @click="saveReferralNotes"
                  >
                    Save Notes
                  </v-btn>
                  <span
                    v-if="notesSaveStatus === 'notes-saved'"
                    class="ml-2 text-caption green--text text--darken-1"
                  >
                    <v-icon x-small color="green darken-1">mdi-check</v-icon>
                    Saved successfully
                  </span>
                  <span
                    v-if="notesSaveStatus === 'notes-error'"
                    class="ml-2 text-caption red--text"
                  >
                    <v-icon x-small color="red">mdi-alert</v-icon>
                    Failed to save
                  </span>
                </div>
              </div>
            </div>
          </GmapInfoWindow>
        </GmapMap>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import gql from "graphql-tag";
import { mapGetters } from "vuex";
import filter from "lodash/filter";

export default {
  data() {
    return {
      search: "",
      organization: { newCasesInPeriod: [] },
      utah: { lat: 40.6146903, lng: -111.816059 },
      idaho: { lat: 43.6216535, lng: -116.2844614 },
      washington: { lat: 47.703469, lng: -117.1408785 },
      mapCenter: { lat: 40.6146903, lng: -111.816059 },
      locationOptions: [
        { text: "Utah", value: "utah" },
        { text: "Idaho", value: "idaho" },
        { text: "Washington", value: "washington" },
      ],
      mapOptions: {
        zoomControl: true,
        mapTypeControl: true,
        scaleControl: true,
        streetViewControl: true,
        rotateControl: true,
      },
      showInfoWindow: false,
      infoWindowPosition: {},
      infoWindowText: "",
      infoWindowContact: {},
      editableReferralNotes: "",
      savingNotes: false,
      notesSaveStatus: "",
      notesReadonly: true,
      searchMarker: false,
      // Filter options
      filterAcceptsLien: false,
      filterAcceptsHealthInsurance: false,
      selectedPriorityLevels: [],
      priorityLevelOptions: [
        { text: "1 - Highest Priority (Green)", value: 1 },
        { text: "2 - High Priority (Blue)", value: 2 },
        { text: "3 - Medium Priority (Yellow)", value: 3 },
        { text: "4 - Low Priority (Red)", value: 4 },
        { text: "5 - Do Not Use (Black)", value: 5 },
      ],
    };
  },
  computed: {
    ...mapGetters(["currentUser", "startDate", "endDate"]),
    location() {
      return "utah";
    },
    hasActiveFilters() {
      return (
        this.selectedPriorityLevels.length > 0 ||
        this.filterAcceptsLien ||
        this.filterAcceptsHealthInsurance ||
        this.search
      );
    },
    activeFilterCount() {
      let count = 0;
      if (this.selectedPriorityLevels.length > 0) count++;
      if (this.filterAcceptsLien) count++;
      if (this.filterAcceptsHealthInsurance) count++;
      if (this.search) count++;
      return count;
    },
    markers() {
      if (!this.contacts || !Array.isArray(this.contacts)) return [];

      let filteredContacts = filter(this.contacts, (c) => {
        // Keep existing priority filter (boolean priority field)
        if (!c.priority) return false;

        // Filter by priority levels if selected
        if (this.selectedPriorityLevels.length > 0) {
          const priorityLevel = c.priority_level
            ? parseInt(c.priority_level)
            : null;
          if (!this.selectedPriorityLevels.includes(priorityLevel)) {
            return false;
          }
        }

        // Filter by accepts lien
        if (this.filterAcceptsLien && !c.accepts_lien) {
          return false;
        }

        // Filter by accepts health insurance
        if (this.filterAcceptsHealthInsurance && !c.accepts_health_insurance) {
          return false;
        }

        return true;
      });

      let markers = filteredContacts
        .filter(
          (c) =>
            c.lat &&
            c.lng &&
            !isNaN(parseFloat(c.lat)) &&
            !isNaN(parseFloat(c.lng))
        )
        .map((c) => {
          return Object.assign({}, c, {
            position: {
              lat: parseFloat(c.lat),
              lng: parseFloat(c.lng),
            },
            title: c.name,
            icon: {
              url: this.getIconUrl(c.priority_level),
            },
          });
        });

      if (this.searchMarker) {
        markers = markers.concat(this.searchMarker);
      }
      return markers;
    },
  },
  methods: {
    changeLocation(e) {
      this.mapCenter = this[e];
    },
    showInfo(e) {
      this.infoWindowContact = e;
      this.editableReferralNotes = e.referral_notes || "";
      this.notesSaveStatus = "";
      this.notesReadonly = true;
      this.showInfoWindow = true;
      this.infoWindowPosition = e.position;
      this.infoWindowText =
        e.title +
        ` Inbound: ${e.inboundReferrals}, Outbound: ${e.outboundReferrals}`;
    },
    closeInfo() {
      this.showInfoWindow = false;
    },
    enableNotesEditing() {
      this.notesReadonly = false;
    },
    saveReferralNotes() {
      this.savingNotes = true;
      this.notesSaveStatus = "";
      this.$apollo
        .mutate({
          mutation: gql`
            mutation updateContact($contactId: Int!, $contact: ContactInput!) {
              updateContact(contactId: $contactId, contact: $contact) {
                id
                referral_notes
              }
            }
          `,
          variables: {
            contactId: parseInt(this.infoWindowContact.id),
            contact: {
              referral_notes: this.editableReferralNotes,
            },
          },
        })
        .then(() => {
          this.savingNotes = false;
          this.notesSaveStatus = "notes-saved";
          this.infoWindowContact.referral_notes = this.editableReferralNotes;
          this.$apollo.queries.contacts.refetch();
          setTimeout(() => {
            this.notesSaveStatus = "";
          }, 3000);
        })
        .catch((error) => {
          this.savingNotes = false;
          this.notesSaveStatus = "notes-error";
          console.error(error);
          setTimeout(() => {
            this.notesSaveStatus = "";
          }, 3000);
        });
    },
    clearAllFilters() {
      this.filterAcceptsLien = false;
      this.filterAcceptsHealthInsurance = false;
      this.selectedPriorityLevels = [];
      this.search = "";
      this.searchMarker = false;
    },
    removePriorityLevel(value) {
      const index = this.selectedPriorityLevels.indexOf(value);
      if (index > -1) {
        this.selectedPriorityLevels.splice(index, 1);
      }
    },
    getPriorityChipColor(priorityLevel) {
      const colorMap = {
        1: "green darken-2", // Highest Priority
        2: "blue darken-2", // High Priority
        3: "yellow darken-3", // Medium Priority
        4: "red darken-2", // Low Priority
        5: "black", // Do Not Use
      };
      return colorMap[priorityLevel] || "grey";
    },
    getIconUrl(priorityLevel) {
      const iconMap = {
        1: "https://maps.google.com/mapfiles/ms/icons/green-dot.png", // Highest Priority
        2: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png", // High Priority
        3: "https://maps.google.com/mapfiles/ms/icons/yellow-dot.png", // Medium Priority
        4: "https://maps.google.com/mapfiles/ms/icons/red-dot.png", // Low Priority
        5: "https://maps.google.com/mapfiles/ms/icons/black-dot.png", // Do Not Use
      };

      const normalizedPriority = priorityLevel ? parseInt(priorityLevel) : null;
      return (
        iconMap[normalizedPriority] ||
        "https://maps.google.com/mapfiles/ms/icons/purple-dot.png"
      );
    },
    getPriorityColor(priorityLevel) {
      const colorMap = {
        1: "green darken-2", // Highest Priority
        2: "blue darken-2", // High Priority
        3: "yellow darken-3", // Medium Priority
        4: "red darken-2", // Low Priority
        5: "black", // Do Not Use
      };

      const normalizedPriority = priorityLevel ? parseInt(priorityLevel) : null;
      return colorMap[normalizedPriority] || "grey";
    },
    searchAddress() {
      let address = this.search.replace(/\s+/g, "+").replace("#", "Suite+");
      let url = `https://maps.googleapis.com/maps/api/geocode/json?address=${address}&key=AIzaSyBnnnNtNSlPQ-z1_Eu-pbSogg5nBzoGzR4`;
      fetch(url)
        .then((response) => response.json())
        .then(({ results }) => {
          if (results.length > 0) {
            let position = results[0].geometry.location;
            this.mapCenter = position;
            this.searchMarker = {
              position,
              icon: {
                url: "https://maps.google.com/mapfiles/ms/icons/pink-dot.png",
              },
            };
          }
        });
    },
  },
  apollo: {
    contacts: {
      query: gql`
        query DashContacts {
          contacts {
            id
            saId
            saCtg
            contactType {
              name
            }
            name
            address
            city
            state
            zip
            phone
            email
            priority
            priority_level
            accepts_lien
            accepts_health_insurance
            inboundReferrals
            outboundReferrals
            referral_notes
            lat
            lng
          }
        }
      `,
      skip() {
        return this.currentUser ? false : true;
      },
    },
  },
};
</script>

<style scoped>
.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
}

.legend-dot.red {
  background-color: #f44336;
}

.legend-dot.orange {
  background-color: #ff9800;
}

.legend-dot.green {
  background-color: #4caf50;
}

.legend-dot.blue {
  background-color: #2196f3;
}

.legend-dot.black {
  background-color: #000000;
}

.legend-dot.yellow {
  background-color: #ffc107;
}

.legend-dot.light-blue {
  background-color: #03a9f4;
}

.legend-dot.purple {
  background-color: #9c27b0;
}

.mini-legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.mini-legend-dot.red {
  background-color: #f44336;
}

.mini-legend-dot.orange {
  background-color: #ff9800;
}

.mini-legend-dot.green {
  background-color: #4caf50;
}

.mini-legend-dot.blue {
  background-color: #2196f3;
}

.mini-legend-dot.black {
  background-color: #000000;
}

.mini-legend-dot.yellow {
  background-color: #ffc107;
}

.mini-legend-dot.light-blue {
  background-color: #03a9f4;
}

.mini-legend-dot.purple {
  background-color: #9c27b0;
}
</style>

<style>
.notes-saved .v-input__slot {
  border-color: #4caf50 !important;
  transition: border-color 0.3s ease;
}

.notes-saved fieldset {
  border-color: #4caf50 !important;
  border-width: 2px !important;
  transition: border-color 0.3s ease;
}

.notes-error .v-input__slot {
  border-color: #f44336 !important;
  transition: border-color 0.3s ease;
}

.notes-error fieldset {
  border-color: #f44336 !important;
  border-width: 2px !important;
  transition: border-color 0.3s ease;
}
</style>
